import React, { useCallback, useContext } from "react";
import { graphql } from "react-relay";
import { useHistory } from "react-router-dom";

import {
	faBookOpen,
	faCalendar,
	faStreetView,
	faThumbsUp,
	faUser,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserFriends } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import cx from "classnames";

import { showFeatureModal } from "~/billing";
import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import type { FeedbackRequestContextType } from "~/feedback/utils";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, toast } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { FEEDBACK_CATEGORY_VALUES } from "./CategoryDropdown";
import { refetchEmitter } from "./FeedbackList";

import type { CreateFeedbackButtonCreateDraftMutation as CreateDraftMutationType } from "~/__generated__/CreateFeedbackButtonCreateDraftMutation.graphql";

import styles from "./CreateFeedbackButton.module.css";

// TODO: We will remove this from here
// The idea is to stop creating an actual draft when clicking to create a new feedback
const CreateDraftMutation = graphql`
	mutation CreateFeedbackButtonCreateDraftMutation($type: Type!) {
		createDraftFeedback(type: $type) {
			feedback {
				id
			}
		}
	}
`;

interface CreateFeedbackButtonProps {
	hasAccessToFeedback: boolean;
	hasAccessTo360Feedback: boolean;
	triggerText?: string;
	triggerClassName?: string;
}

export default function CreateFeedbackButton({
	hasAccessToFeedback,
	hasAccessTo360Feedback,
	triggerText,
	triggerClassName,
}: CreateFeedbackButtonProps) {
	const { isMobile } = useContext(ViewportContext);
	const { user } = useContext(AppContext);

	const history = useHistory();

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);

	const create360Feedback = useCallback(() => {
		if (!hasAccessTo360Feedback) {
			showFeatureModal("three_sixty_feedback");
			return;
		}
		history.push(`/feedback/three-sixty/`);
	}, [hasAccessTo360Feedback, history]);

	const show360Toast = useCallback(() => toast.error("360 Feedback cannot be created on mobile"), []);

	// TODO: We will remove this from here
	// The idea is to stop creating an actual draft when clicking to create a new feedback
	const createDraftFeedback = useCallback((type: CreateDraftMutationType["variables"]["type"]) => {
		return new Promise((resolve, reject) => {
			commitMutationPromise<CreateDraftMutationType>(environment, {
				mutation: CreateDraftMutation,
				variables: { type },
				onError: e => reject([e]),
				onCompleted: ({ createDraftFeedback }, errors) => {
					if (errors) {
						reject(errors);
						return;
					}

					if (createDraftFeedback == null) return;
					const {
						feedback: { id },
					} = createDraftFeedback;

					refetchEmitter.emit("FeedbackListRefetch", FEEDBACK_CATEGORY_VALUES.DRAFT);

					resolve(id);
				},
			});
		});
	}, []);

	const createDirectFeedback = useCallback(async () => {
		if (!hasAccessToFeedback) {
			showFeatureModal("feedback");
			return;
		}
		try {
			const id = await createDraftFeedback("UNSOLICITED");
			history.push(`/feedback/${id}/draft/`);
		} catch (errors) {
			if (Array.isArray(errors)) {
				toast.error(`Could not give feedback: ${errors[0].message}`);
			}
		}
	}, [createDraftFeedback, hasAccessToFeedback, history]);

	const createFeedbackRequest = useCallback(
		async (context?: FeedbackRequestContextType) => {
			if (!hasAccessToFeedback) {
				showFeatureModal("feedback");
				return;
			}
			try {
				const id = await createDraftFeedback("REQUESTED");
				history.push(`/feedback/${id}/draft/`, { context: context });
			} catch (errors) {
				if (Array.isArray(errors)) {
					toast.error(`Could not create feedback request: ${errors[0].message}`);
				}
			}
		},
		[createDraftFeedback, hasAccessToFeedback, history],
	);

	const askAboutMyself = useCallback(() => createFeedbackRequest("myself"), [createFeedbackRequest]);

	const askAboutDirectReport = useCallback(() => createFeedbackRequest("report"), [createFeedbackRequest]);

	const askAboutMeeting = useCallback(() => createFeedbackRequest("event"), [createFeedbackRequest]);

	const askAboutSomethingElse = useCallback(() => createFeedbackRequest("other"), [createFeedbackRequest]);

	return (
		<Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
			<Menu.Trigger>
				{triggerText ? (
					<Button
						size="normal"
						variant={!hasAccessToFeedback ? "billing" : "primary"}
						className={cx(styles.trigger, triggerClassName)}
						data-test-id="feedback-panel-new-feedback-button"
					>
						{triggerText}
					</Button>
				) : (
					<IconButton
						variant={!hasAccessToFeedback ? "billing" : "primary"}
						className={cx(styles.trigger, styles.iconButton, triggerClassName, {
							[styles.billing]: !hasAccessToFeedback,
						})}
						data-test-id="feedback-panel-new-feedback"
						icon={faPlus}
						title="Create"
					/>
				)}
			</Menu.Trigger>
			<Menu.Content data-test-id="feedback-panel-menu">
				{user?.internalAttributes?.isManager && (
					<Menu.Item icon={faStreetView} onSelect={isMobile ? show360Toast : create360Feedback}>
						360 feedback
					</Menu.Item>
				)}
				<Menu.Item icon={faThumbsUp} onSelect={createDirectFeedback}>
					Give feedback
				</Menu.Item>
				<Menu.Item icon={faUser} onSelect={askAboutMyself}>
					Ask about myself
				</Menu.Item>
				{user?.internalAttributes?.isManager && (
					<Menu.Item icon={faUserFriends} onSelect={askAboutDirectReport}>
						Ask about someone on my team
					</Menu.Item>
				)}
				<Menu.Item icon={faCalendar} onSelect={askAboutMeeting}>
					Ask about a meeting
				</Menu.Item>
				<Menu.Item icon={faBookOpen} onSelect={askAboutSomethingElse}>
					Ask about something else
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
